/**
 * Asset Loader Service
 * 
 * This service provides a bridge between whowset's asset management
 * and React components. It reads the whowset-cvs.json artifact and
 * provides helper functions to resolve asset URLs with theme/language support.
 */

import type {
    WhowsetCvsJson,
    WhowsetTheme,
    WhowsetLanguage,
    AssetOptions,
    ResolvedAsset,
} from '../types/whowset'
import { logger } from '../utils/logger'

class AssetLoaderService {
    private assets: WhowsetCvsJson = {}
    private loaded = false
    private readonly listeners = new Set<() => void>()
    private defaultTheme: WhowsetTheme = 'default'
    private defaultLanguage: WhowsetLanguage = 'global'
    private baseUrl = ''

    public readonly subscribe = (listener: () => void): (() => void) => {
        this.listeners.add(listener)

        return () => {
            this.listeners.delete(listener)
        }
    }

    public readonly getSnapshot = (): boolean => this.loaded

    /**
     * Initialize the asset loader with whowset-cvs.json
     */
    public async initialize(cvsJsonPath: string = '/whowset-cvs.json'): Promise<void> {
        if (this.loaded) {
            return
        }

        try {
            const response = await fetch(cvsJsonPath)
            if (!response.ok) {
                throw new Error(`Failed to load whowset-cvs.json: ${response.statusText}`)
            }
            this.assets = await response.json()
            this.loaded = true

            for (const listener of this.listeners) {
                listener()
            }
            logger.log('ASSET', 'Initialized with', Object.keys(this.assets).length, 'assets')
        } catch (error) {
            logger.error('ASSET', 'Failed to initialize:', error)
            // Don't throw - allow graceful degradation
        }
    }

    /**
     * Set the default theme for asset resolution
     */
    public setDefaultTheme(theme: WhowsetTheme): void {
        this.defaultTheme = theme
    }

    /**
     * Set the default language for asset resolution
     */
    public setDefaultLanguage(language: WhowsetLanguage): void {
        this.defaultLanguage = language
    }

    /**
     * Set the base URL for assets (useful for CDN or different environments)
     */
    public setBaseUrl(url: string): void {
        this.baseUrl = url
    }

    /**
     * Get an asset URL with theme/language resolution
     * 
     * @param assetPath - The asset path from whowset (e.g., "assets/cobrand/vip-room/logo-suffix.png")
     * @param options - Theme/language options
     * @returns Resolved asset information or null if not found
     */
    public getAsset(assetPath: string, options: AssetOptions = {}): ResolvedAsset | null {
        if (!this.loaded) {
            logger.warn('ASSET', 'Not initialized. Call initialize() first.')
            return null
        }

        const asset = this.assets[assetPath]
        if (!asset) {
            logger.warn('ASSET', `Asset not found: ${assetPath}`)
            return null
        }

        // Resolve theme with fallback
        const requestedTheme = options.theme || this.defaultTheme
        const fallbackTheme = options.fallbackTheme || 'default'
        
        let themeMap = asset[requestedTheme]
        if (!themeMap && requestedTheme !== fallbackTheme) {
            themeMap = asset[fallbackTheme]
        }
        
        if (!themeMap) {
            // Try first available theme
            const firstTheme = Object.keys(asset)[0] as WhowsetTheme
            themeMap = asset[firstTheme]
            if (!themeMap) {
                logger.warn('ASSET', `No theme found for asset: ${assetPath}`)
                return null
            }
        }

        // Resolve language with fallback
        const requestedLanguage = options.language || this.defaultLanguage
        const fallbackLanguage = options.fallbackLanguage || 'global'
        
        let variant = themeMap[requestedLanguage]
        if (!variant && requestedLanguage !== fallbackLanguage) {
            variant = themeMap[fallbackLanguage]
        }
        
        if (!variant) {
            // Try first available language
            const firstLanguage = Object.keys(themeMap)[0] as WhowsetLanguage
            variant = themeMap[firstLanguage]
            if (!variant) {
                logger.warn('ASSET', `No language variant found for asset: ${assetPath}`)
                return null
            }
        }

        const [url, contentVersion] = variant
        // Ensure proper URL concatenation with baseUrl
        const separator = this.baseUrl && !this.baseUrl.endsWith('/') && !url.startsWith('/') ? '/' : ''
        const fullUrl = this.baseUrl + separator + url + (contentVersion ? `?__cv=${contentVersion}` : '')

        return {
            url,
            contentVersion,
            theme: requestedTheme,
            language: requestedLanguage,
            fullUrl,
        }
    }

    /**
     * Get asset URL directly (convenience method)
     */
    public getAssetUrl(assetPath: string, options: AssetOptions = {}): string | null {
        const asset = this.getAsset(assetPath, options)
        return asset ? asset.fullUrl : null
    }

    /**
     * Check if an asset exists
     */
    public hasAsset(assetPath: string): boolean {
        return !!this.assets[assetPath]
    }

    /**
     * Get all available assets (for debugging)
     */
    public getAllAssets(): string[] {
        return Object.keys(this.assets)
    }

    /**
     * Search assets by pattern (for debugging/discovery)
     */
    public searchAssets(pattern: string | RegExp): string[] {
        const regex = typeof pattern === 'string' ? new RegExp(pattern, 'i') : pattern
        return this.getAllAssets().filter((path) => regex.test(path))
    }

    /**
     * Find asset by slug - intelligent matching for iconSlug values
     * 
     * @param slug - The slug to search for (e.g., "chips-7")
     * @param searchPath - Optional path prefix to narrow search (e.g., "shop/item")
     * @returns The best matching asset path or null
     */
    public findAssetBySlug(slug: string, searchPath: string = 'shop/item'): string | null {
        if (!this.loaded || !slug) {
            return null
        }

        const allAssets = this.getAllAssets()
        
        // Filter assets that match the search path
        const relevantAssets = allAssets.filter((path) => path.includes(searchPath))
        
        // Normalize the slug for comparison (remove special chars, lowercase)
        const normalizedSlug = slug.toLowerCase().replace(/[_-]/g, '')
        
        // Try to find exact or close matches
        let bestMatch: string | null = null
        let bestScore = 0
        
        for (const assetPath of relevantAssets) {
            // Extract filename without extension
            const filename = assetPath.split('/').pop()?.replace(/\.[^.]+$/, '') || ''
            const normalizedFilename = filename.toLowerCase().replace(/[_-]/g, '')
            
            // Calculate similarity score
            let score = 0
            
            // Exact match (after normalization)
            if (normalizedFilename === normalizedSlug) {
                return assetPath // Perfect match, return immediately
            }
            
            // Contains the slug
            if (normalizedFilename.includes(normalizedSlug)) {
                score = 50 + (normalizedSlug.length / normalizedFilename.length) * 50
            }
            
            // Slug contains filename
            if (normalizedSlug.includes(normalizedFilename)) {
                score = 40 + (normalizedFilename.length / normalizedSlug.length) * 40
            }
            
            // Check for numeric patterns (chips-7 -> chip_amount7)
            const slugNumbers = slug.match(/\d+/g)
            const filenameNumbers = filename.match(/\d+/g)
            if (slugNumbers && filenameNumbers && slugNumbers[0] === filenameNumbers[0]) {
                score += 30
            }
            
            // Check for word matches (chips -> chip)
            const slugWords = slug.toLowerCase().split(/[-_]/)
            const filenameWords = filename.toLowerCase().split(/[-_]/)
            const matchingWords = slugWords.filter((word) => 
                filenameWords.some((fw) => fw.includes(word) || word.includes(fw))
            ).length
            score += (matchingWords / slugWords.length) * 20
            
            if (score > bestScore) {
                bestScore = score
                bestMatch = assetPath
            }
        }
        
        // Only return if we have a reasonable match (score > 40)
        if (bestScore > 40) {
            logger.log('ASSET', `Found match for "${slug}": "${bestMatch}" (score: ${bestScore.toFixed(0)})`)
            return bestMatch
        }
        
        logger.warn('ASSET', `No good match found for slug: "${slug}" in ${searchPath}`)
        return null
    }
}

// Export singleton instance
export const assetLoader = new AssetLoaderService()

// Export the class for testing
export default AssetLoaderService

